import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Mail, Phone, CheckCircle, Clock, MessageSquare, Settings, Save, Upload, Eye } from 'lucide-react';
import api from '../../utils/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { useAuth } from '../../context/AuthContext';
import { Field, Input, Textarea } from '../components/FormField';

const STATUS_CONFIG = {
  new:     { label: 'New',     color: '#10B981', bg: '#D1FAE5' },
  read:    { label: 'Read',    color: '#6B7280', bg: '#F3F4F6' },
  replied: { label: 'Replied', color: '#3B82F6', bg: '#DBEAFE' },
  closed:  { label: 'Closed',  color: '#9CA3AF', bg: '#F9FAFB' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

const DEFAULT_TEMPLATE = {
  smsHeader: 'Dear {{name}}, your House of Sample inquiry {{inquiryNumber}} has been received.',
  smsBody: 'Our team will contact you within 1 business day.',
  smsVisitUrl: 'https://www.houseofsample.in',
  smsWriteEmail: 'sales@houseofsample.in',
  smsReachPhone: '+91 85114 28123',
  emailHeader: 'Thank You for Your Inquiry — {{inquiryNumber}}',
  emailBody: 'Dear {{name}},\n\nWe have received your inquiry and our team will get back to you within 1 business day.\n\nThank you for reaching out to House of Sample.',
  emailLogoUrl: '',
  emailVisitUrl: 'https://www.houseofsample.in',
  emailWriteEmail: 'sales@houseofsample.in',
  emailReachPhone: '+91 85114 28123',
};

const interpolate = (str, vars) =>
  str?.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] || `{{${k}}}`) || '';

const SAMPLE_VARS = { name: 'John Doe', inquiryNumber: 'HOS-20250621-0001' };

export default function AdminContacts() {
  const { can, isMasterAdmin } = useAuth();
  const canDelete = can('contacts', 'delete');
  const [tab, setTab]           = useState('inquiries');
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [permDenied, setPermDenied] = useState(false);

  // Template state
  const [tmpl, setTmpl]         = useState(DEFAULT_TEMPLATE);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = () => api.get('/contact')
    .then(r => setContacts(r.data.contacts || []))
    .catch(err => { if (err.response?.status === 403) setPermDenied(true); });

  const loadTemplate = () => {
    if (!isMasterAdmin) return;
    api.get('/contact/templates')
      .then(r => { if (r.data.template) setTmpl(r.data.template); })
      .catch(() => {});
  };

  useEffect(() => { load(); loadTemplate(); }, []);

  const handleView = async (c) => {
    setSelected(c);
    if (c.status === 'new') {
      await api.put(`/contact/${c._id}/status`, { status: 'read' }).catch(() => {});
      load();
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/contact/${id}/status`, { status });
      load();
      if (selected?._id === id) setSelected(s => ({ ...s, status }));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    await api.delete(`/contact/${id}`); toast.success('Deleted'); load();
    if (selected?._id === id) setSelected(null);
  };

  const handleSaveSms = async () => {
    setTmplLoading(true);
    try {
      const res = await api.put('/contact/templates', {
        smsHeader: tmpl.smsHeader, smsBody: tmpl.smsBody,
        smsVisitUrl: tmpl.smsVisitUrl, smsWriteEmail: tmpl.smsWriteEmail, smsReachPhone: tmpl.smsReachPhone,
      });
      setTmpl(res.data.template);
      toast.success('SMS template saved');
    } catch { toast.error('Failed to save SMS template'); }
    finally { setTmplLoading(false); }
  };

  const handleSaveEmail = async () => {
    setTmplLoading(true);
    try {
      const res = await api.put('/contact/templates', {
        emailHeader: tmpl.emailHeader, emailBody: tmpl.emailBody, emailLogoUrl: tmpl.emailLogoUrl,
        emailVisitUrl: tmpl.emailVisitUrl, emailWriteEmail: tmpl.emailWriteEmail, emailReachPhone: tmpl.emailReachPhone,
      });
      setTmpl(res.data.template);
      toast.success('Email template saved');
    } catch { toast.error('Failed to save email template'); }
    finally { setTmplLoading(false); }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    const fd = new FormData(); fd.append('logo', logoFile);
    try {
      const res = await api.post('/contact/templates/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTmpl(t => ({ ...t, emailLogoUrl: res.data.logoUrl }));
      setLogoFile(null); toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
    finally { setLogoUploading(false); }
  };

  const counts = {
    all:     contacts.length,
    new:     contacts.filter(c => c.status === 'new').length,
    read:    contacts.filter(c => c.status === 'read').length,
    replied: contacts.filter(c => c.status === 'replied').length,
    closed:  contacts.filter(c => c.status === 'closed').length,
  };

  const columns = [
    { key: 'inquiryNumber', label: 'Ref #',
      render: v => v
        ? <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">{v}</span>
        : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'name', label: 'Name', isTitle: true,
      render: (v) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold shrink-0">
            {v?.[0]?.toUpperCase()}
          </div>
          <span className="text-sm font-medium text-gray-800">{v}</span>
        </div>
      )
    },
    { key: 'email', label: 'Email',
      render: v => <span className="flex items-center gap-1 text-xs text-gray-500"><Mail size={11}/>{v}</span>
    },
    { key: 'phone', label: 'Phone',
      render: v => v ? <span className="flex items-center gap-1 text-xs text-gray-500"><Phone size={11}/>{v}</span> : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
    { key: 'phoneVerified', label: 'Verified',
      render: (v, row) => (
        <div className="flex gap-1">
          <span className={`text-xs px-1.5 py-0.5 rounded ${row.emailVerified ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
            {row.emailVerified ? '✓ Email' : '✗ Email'}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${v ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
            {v ? '✓ Phone' : '✗ Phone'}
          </span>
        </div>
      )
    },
    { key: 'createdAt', label: 'Date', render: v => new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) },
  ];

  const summaryFields = [
    { key: 'all',     label: 'Total',   color: '#4F8EF7', render: data => data.length },
    { key: 'new',     label: 'New',     color: '#10B981', render: data => data.filter(c => c.status === 'new').length },
    { key: 'replied', label: 'Replied', color: '#3B82F6', render: data => data.filter(c => c.status === 'replied').length },
    { key: 'closed',  label: 'Closed',  color: '#9CA3AF', render: data => data.filter(c => c.status === 'closed').length },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Inquiries</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {counts.all} total &nbsp;·&nbsp;
            {counts.new > 0 && <span className="text-emerald-500 font-medium">{counts.new} new</span>}
          </p>
        </div>
        {/* Tabs */}
        <div className="flex gap-2">
          <button onClick={() => setTab('inquiries')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors
              ${tab === 'inquiries' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <MessageSquare size={14} /> Inquiries
          </button>
          {isMasterAdmin && (
            <button onClick={() => setTab('templates')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors
                ${tab === 'templates' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <Settings size={14} /> Templates
            </button>
          )}
        </div>
      </div>

      {/* ── INQUIRIES TAB ─────────────────────────────────────────────────── */}
      {tab === 'inquiries' && (
        permDenied ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <p className="text-amber-700 font-semibold text-sm">Access Restricted</p>
            <p className="text-amber-600 text-xs mt-1">You don't have permission to view inquiries.</p>
          </div>
        ) : (
          <DataTable
            columns={columns} data={contacts}
            onView={handleView}
            onDelete={canDelete ? handleDelete : null}
            searchKeys={['name','email','phone','inquiryNumber']}
            summaryFields={summaryFields}
            defaultView="table"
          />
        )
      )}

      {/* ── TEMPLATES TAB ─────────────────────────────────────────────────── */}
      {tab === 'templates' && isMasterAdmin && (
        <div className="grid lg:grid-cols-2 gap-6">

          {/* SMS Template */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                <Phone size={14} className="text-blue-500" /> SMS Template
              </h2>
              <button type="button" onClick={() => setShowPreview(p => !p)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                <Eye size={12} /> {showPreview ? 'Hide' : 'Preview'}
              </button>
            </div>
            <Field label="1. Message Header (use {{inquiryNumber}} and {{name}})">
              <Textarea rows={2} value={tmpl.smsHeader} onChange={e => setTmpl(t => ({ ...t, smsHeader: e.target.value }))} />
            </Field>
            <Field label="2. Customised Message">
              <Textarea rows={3} value={tmpl.smsBody} onChange={e => setTmpl(t => ({ ...t, smsBody: e.target.value }))} />
            </Field>
            <Field label="3. Visit us at">
              <Input value={tmpl.smsVisitUrl} onChange={e => setTmpl(t => ({ ...t, smsVisitUrl: e.target.value }))} placeholder="https://www.houseofsample.in" />
            </Field>
            <Field label="4. Write us at">
              <Input value={tmpl.smsWriteEmail} onChange={e => setTmpl(t => ({ ...t, smsWriteEmail: e.target.value }))} placeholder="sales@houseofsample.in" />
            </Field>
            <Field label="5. Reach us at">
              <Input value={tmpl.smsReachPhone} onChange={e => setTmpl(t => ({ ...t, smsReachPhone: e.target.value }))} placeholder="+91 85114 28123" />
            </Field>
            {showPreview && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">SMS Preview</p>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {[
                    interpolate(tmpl.smsHeader, SAMPLE_VARS),
                    interpolate(tmpl.smsBody, SAMPLE_VARS),
                    tmpl.smsVisitUrl   ? `Visit us at: ${tmpl.smsVisitUrl}` : '',
                    tmpl.smsWriteEmail ? `Write us at: ${tmpl.smsWriteEmail}` : '',
                    tmpl.smsReachPhone ? `Reach us at: ${tmpl.smsReachPhone}` : '',
                  ].filter(Boolean).join('\n')}
                </pre>
              </div>
            )}
            <button onClick={handleSaveSms} disabled={tmplLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Save size={14} /> {tmplLoading ? 'Saving...' : 'Save SMS Template'}
            </button>
          </div>

          {/* Email Template */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                <Mail size={14} className="text-yellow-600" /> Email Template
              </h2>
              <button type="button" onClick={() => setShowPreview(p => !p)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                <Eye size={12} /> {showPreview ? 'Hide' : 'Preview'}
              </button>
            </div>
            <Field label="1. Email Header (use {{inquiryNumber}} and {{name}})">
              <Input value={tmpl.emailHeader} onChange={e => setTmpl(t => ({ ...t, emailHeader: e.target.value }))} />
            </Field>
            <Field label="2. Customised Message">
              <Textarea rows={4} value={tmpl.emailBody} onChange={e => setTmpl(t => ({ ...t, emailBody: e.target.value }))} />
            </Field>
            <Field label="3. Brand Logo">
              <div className="space-y-2">
                {tmpl.emailLogoUrl && (
                  <img src={tmpl.emailLogoUrl} alt="Logo preview" className="h-12 object-contain border border-gray-200 rounded p-1" />
                )}
                <div className="flex gap-2">
                  <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])}
                    className="flex-1 text-sm text-gray-600 border border-gray-300 rounded px-2 py-1" />
                  <button type="button" onClick={handleLogoUpload} disabled={!logoFile || logoUploading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white text-xs rounded hover:bg-gray-800 disabled:opacity-50">
                    <Upload size={12} /> {logoUploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </Field>
            <Field label="4. Visit us at">
              <Input value={tmpl.emailVisitUrl} onChange={e => setTmpl(t => ({ ...t, emailVisitUrl: e.target.value }))} />
            </Field>
            <Field label="5. Write us at">
              <Input value={tmpl.emailWriteEmail} onChange={e => setTmpl(t => ({ ...t, emailWriteEmail: e.target.value }))} />
            </Field>
            <Field label="6. Reach us at">
              <Input value={tmpl.emailReachPhone} onChange={e => setTmpl(t => ({ ...t, emailReachPhone: e.target.value }))} />
            </Field>
            {showPreview && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Email Preview</p>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {[
                    interpolate(tmpl.emailHeader, SAMPLE_VARS),
                    '',
                    interpolate(tmpl.emailBody, SAMPLE_VARS),
                    '',
                    tmpl.emailVisitUrl   ? `Visit us at: ${tmpl.emailVisitUrl}`   : '',
                    tmpl.emailWriteEmail ? `Write us at: ${tmpl.emailWriteEmail}` : '',
                    tmpl.emailReachPhone ? `Reach us at: ${tmpl.emailReachPhone}` : '',
                  ].filter((line, i, arr) => !(line === '' && arr[i - 1] === '')).join('\n')}
                </pre>
              </div>
            )}
            <button onClick={handleSaveEmail} disabled={tmplLoading}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors">
              <Save size={14} /> {tmplLoading ? 'Saving...' : 'Save Email Template'}
            </button>
          </div>
        </div>
      )}

      {/* ── INQUIRY DETAIL MODAL ──────────────────────────────────────────── */}
      {selected && (
        <Modal title="Inquiry Details" onClose={() => setSelected(null)}>
          <div className="space-y-4">
            {selected.inquiryNumber && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
                <p className="text-xs text-amber-600 uppercase tracking-widest mb-1">Inquiry Reference</p>
                <p className="font-mono text-lg font-bold text-amber-700 tracking-widest">{selected.inquiryNumber}</p>
              </div>
            )}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-lg font-bold">
                {selected.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-gray-800">{selected.name}</p>
                <p className="text-xs text-gray-400">{new Date(selected.createdAt).toLocaleString()}</p>
              </div>
              <div className="ml-auto"><StatusBadge status={selected.status} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <Mail size={10} /> Email {selected.emailVerified && <CheckCircle size={10} className="text-emerald-500" />}
                </p>
                <p className="text-gray-700 font-medium break-all text-xs">{selected.email}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <Phone size={10} /> Phone {selected.phoneVerified && <CheckCircle size={10} className="text-blue-500" />}
                </p>
                <p className="text-gray-700 font-medium text-xs">{selected.phone || '—'}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-2">Message</p>
              <p className="text-gray-700 text-sm leading-relaxed">{selected.message}</p>
            </div>
            {/* Status update */}
            <div>
              <p className="text-xs text-gray-500 mb-2">Update Status</p>
              <div className="flex gap-2 flex-wrap">
                {Object.keys(STATUS_CONFIG).map(s => (
                  <button key={s} onClick={() => handleStatusChange(selected._id, s)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors
                      ${selected.status === s ? 'border-transparent text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                    style={selected.status === s ? { background: STATUS_CONFIG[s].color } : {}}>
                    {STATUS_CONFIG[s].label}
                  </button>
                ))}
              </div>
            </div>
            {canDelete && (
              <button onClick={() => { handleDelete(selected._id); setSelected(null); }}
                className="w-full py-2 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                Delete Inquiry
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
